import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ShieldCheck, ArrowRight, TrendingUp, Building2 } from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KEY_COLORS = {
  rojo: "bg-rose-500",
  amber: "bg-amber-500",
  verde: "bg-emerald-500",
};

const KEY_BORDER = {
  rojo: "border-rose-200",
  amber: "border-amber-200",
  verde: "border-emerald-200",
};

const KEY_TEXT = {
  rojo: "text-rose-900",
  amber: "text-amber-900",
  verde: "text-emerald-900",
};

export default function Transparencia() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/stats/transparencia`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ total_sitios_escaneados: 0, distribucion: [], por_rubro: [], promedio_general: 0 }));
  }, []);

  if (!data) {
    return <div className="min-h-[60vh] grid place-items-center text-sm text-slate-500">Cargando estadísticas…</div>;
  }

  const total = data.total_sitios_escaneados || 0;
  const promedio = data.promedio_general || 0;

  return (
    <section data-testid="transparencia-page">
      <SEO
        title="Transparencia Ley 21.719 — Estado del cumplimiento en Chile · Ley de Datos Chile"
        description={`Datos agregados y anonimizados de cumplimiento de la Ley N° 21.719 en Chile: ${total} sitios escaneados, score promedio ${promedio}/100. Estadísticas por rubro.`}
        canonical="https://leydedatoschile.com/transparencia"
        keywords="Ley 21.719 estadísticas, cumplimiento datos Chile, transparencia PYME, APDP estado"
      />

      {/* Hero dark */}
      <div className="relative bg-ink-950 text-white overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-16 lg:pt-24 lg:pb-20">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-6">
            <span className="live-dot inline-block" />
            Transparencia · datos agregados
          </div>
          <h1 data-testid="transparencia-title" className="font-display text-5xl sm:text-6xl text-white leading-[1.02] tracking-tight max-w-4xl">
            Estado del cumplimiento Ley 21.719 en <em className="italic text-blue-300">Chile</em>.
          </h1>
          <p className="mt-7 text-lg text-slate-300 leading-relaxed max-w-2xl">
            Datos agregados y anonimizados de los sitios web chilenos escaneados. Los números reflejan la realidad del país a {new Date().toLocaleDateString("es-CL", { day: "numeric", month: "long", year: "numeric" })}.
          </p>
        </div>
      </div>

      <div className="px-6 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto">
          {/* KPIs principales — operating tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-12">
            <Kpi
              icon={Building2}
              value={total.toLocaleString("es-CL")}
              label="Sitios web escaneados"
              tone="info"
              testid="kpi-total"
            />
            <Kpi
              icon={TrendingUp}
              value={`${promedio} / 100`}
              label="Score promedio de cumplimiento"
              tone={promedio >= 70 ? "emerald" : promedio >= 40 ? "amber" : "rose"}
              testid="kpi-promedio"
            />
            <Kpi
              icon={ShieldCheck}
              value={`${pct(getCount(data.distribucion, "verde"), total)}%`}
              label="Cumplen el estándar mínimo (≥70)"
              tone="emerald"
              testid="kpi-cumplen"
            />
          </div>

          {/* Distribución por rango */}
          {data.distribucion?.length > 0 && total > 0 && (
            <section className="mb-12">
              <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-4">
                ▸ Distribución de cumplimiento
              </p>
              <h2 className="font-display text-2xl sm:text-3xl text-slate-900 tracking-tight mb-1 leading-tight">
                ¿Cuántos sitios están en cada <em className="italic text-blue-700">rango</em>?
              </h2>
              <p className="text-sm text-slate-500 mb-7">Distribución total entre los {total.toLocaleString("es-CL")} sitios escaneados.</p>

              <div className="space-y-3">
                {data.distribucion.map((d) => {
                  const p = pct(d.count, total);
                  return (
                    <motion.div
                      key={d.key}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                      data-testid={`dist-${d.key}`}
                      className={`rounded-2xl border ${KEY_BORDER[d.key]} bg-white p-4 sm:p-5`}
                    >
                      <div className="flex items-center justify-between gap-4 mb-3">
                        <span className={`font-display text-base sm:text-lg ${KEY_TEXT[d.key]}`}>{d.rango}</span>
                        <span className={`font-display text-xl sm:text-2xl tabular-nums ${KEY_TEXT[d.key]}`}>
                          {d.count.toLocaleString("es-CL")} <span className="text-sm font-mono opacity-70">({p}%)</span>
                        </span>
                      </div>
                      <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${p}%` }}
                          transition={{ duration: 0.8, ease: "easeOut" }}
                          className={`h-full ${KEY_COLORS[d.key]}`}
                        />
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Por rubro */}
          {data.por_rubro?.length > 0 && (
            <section className="mb-12">
              <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-4">
                ▸ Ranking por rubro
              </p>
              <h2 className="font-display text-2xl sm:text-3xl text-slate-900 tracking-tight mb-1 leading-tight">
                Sectores con más de <em className="italic text-blue-700">3 escaneos</em>.
              </h2>
              <p className="text-sm text-slate-500 mb-7">
                Datos anónimos · solo promedios por rubro.
              </p>

              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50/60 border-b border-slate-200">
                    <tr className="text-left font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                      <th className="px-5 py-3">#</th>
                      <th className="px-5 py-3">Rubro</th>
                      <th className="px-5 py-3 text-right">Empresas</th>
                      <th className="px-5 py-3 text-right">Score promedio</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.por_rubro.map((r, i) => {
                      const c = r.promedio >= 70 ? "text-emerald-700" : r.promedio >= 40 ? "text-amber-700" : "text-rose-700";
                      return (
                        <tr key={r.rubro} data-testid={`rubro-${i}`} className="hover:bg-slate-50/50">
                          <td className="px-5 py-3 text-slate-400 font-mono">{String(i + 1).padStart(2, "0")}</td>
                          <td className="px-5 py-3 font-medium text-slate-900">{r.rubro}</td>
                          <td className="px-5 py-3 text-right text-slate-600 tabular-nums">{r.empresas}</td>
                          <td className={`px-5 py-3 text-right font-display text-lg tabular-nums ${c}`}>{r.promedio}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {data.por_rubro?.length === 0 && (
            <p className="text-sm text-slate-500 mb-12 rounded-2xl bg-slate-50 border border-slate-200 p-5">
              Aún no tenemos suficientes datos por rubro para mostrar (mínimo 3 escaneos vinculados a una empresa registrada). Cuando alcancemos ese mínimo se publicará el ranking sectorial.
            </p>
          )}

          {/* Metodología */}
          <details className="mb-10 rounded-2xl bg-slate-50 border border-slate-200 p-5">
            <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-[0.18em] text-slate-700">Metodología y privacidad</summary>
            <div className="mt-3 text-sm text-slate-600 space-y-2 leading-relaxed">
              <p>Los datos provienen de los escaneos hechos en nuestro analizador gratuito de sitios web chilenos. <strong>Ninguna empresa específica es identificada</strong> en esta página — sólo se muestran agregados.</p>
              <p>El score se calcula automáticamente a partir de señales técnicas visibles (presencia de política de privacidad, banner de cookies, HTTPS, scripts de terceros, headers de seguridad, formularios con consentimiento) y un análisis con IA conforme a la Ley N° 21.719.</p>
              <p>Los rubros vienen del onboarding voluntario de los usuarios Pro registrados. Se publica solo si hay al menos 3 escaneos por rubro (privacidad estadística).</p>
            </div>
          </details>

          {/* CTA */}
          <div className="rounded-2xl bg-ink-950 text-white p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center gap-6 justify-between relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
            <div className="relative">
              <p className="font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-2">¿Dónde queda tu empresa?</p>
              <h3 className="font-display text-2xl sm:text-3xl text-white tracking-tight">
                Mira tu score frente al <em className="italic text-blue-300">promedio</em>.
              </h3>
              <p className="mt-2 text-white/70 max-w-xl text-sm">
                Escanea tu sitio gratis y compara con los {total.toLocaleString("es-CL")} sitios analizados.
              </p>
            </div>
            <Link to="/" className="relative h-12 px-6 rounded-full bg-white text-slate-900 font-semibold inline-flex items-center gap-2 shrink-0 hover:bg-slate-100 transition-colors">
              Escanear gratis <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function Kpi({ icon: Icon, value, label, tone = "info", testid }) {
  const tones = {
    info: { box: "border-slate-200 bg-white", text: "text-slate-900", dot: "bg-blue-500", label: "text-slate-500" },
    emerald: { box: "border-emerald-200 bg-emerald-50/60", text: "text-emerald-700", dot: "bg-emerald-500", label: "text-emerald-700" },
    amber: { box: "border-amber-200 bg-amber-50/60", text: "text-amber-700", dot: "bg-amber-500", label: "text-amber-700" },
    rose: { box: "border-rose-200 bg-rose-50/60", text: "text-rose-700", dot: "bg-rose-500", label: "text-rose-700" },
  };
  const t = tones[tone] || tones.info;
  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid={testid}
      className={`rounded-2xl border ${t.box} p-5 sm:p-6 hover:shadow-sm transition-shadow`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${t.label}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
          {label}
        </div>
        <Icon className="w-3.5 h-3.5 text-slate-400" />
      </div>
      <div className={`mt-3 font-display text-3xl sm:text-4xl tabular-nums leading-none tracking-tight ${t.text}`}>{value}</div>
    </motion.div>
  );
}

function pct(part, total) {
  if (!total) return 0;
  return Math.round((part / total) * 1000) / 10;
}

function getCount(distribucion, key) {
  const r = (distribucion || []).find((d) => d.key === key);
  return r ? r.count : 0;
}
